import React from 'react';
import { FaChartPie, FaCheckCircle, FaDollarSign, FaCoins, FaGem, FaRocket } from 'react-icons/fa';
import { useDolphin } from '../../hooks/useDolphin';



export default function Overview() {
  const { stats } = useDolphin();
  // Removed all unused variables and icon imports per ESLint
  const miningProgress = parseFloat(stats.totalSupply) > 0 
    ? (parseFloat(stats.minedTokens) / parseFloat(stats.totalSupply)) * 100 
    : 0;

  // Referral analytics removed from Overview (now in Admin Analytics tab)

  return (
    <section className="w-full max-w-5xl mx-auto p-0">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-xl font-bold text-cyan-700 dark:text-cyan-200">
          <FaChartPie className="opacity-70"/> System Overview
        </h2>
      </div>
      <div className="flex flex-wrap gap-4">
        {/* Current Price Card */}
        <div className="flex flex-col bg-white/80 dark:bg-gray-900/80 rounded-lg p-3 border border-gray-200 dark:border-gray-800 shadow-sm min-w-[220px] max-w-xs flex-1">
          <div className="flex items-center gap-2 mb-1">
            <FaDollarSign className="text-cyan-500" />
            <span className="font-semibold text-gray-700 dark:text-gray-200">Current Token Price</span>
          </div>
          <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-300 mb-1">${Number(stats.currentPrice).toFixed(2)}</div>
          <div className="h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden mb-1">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full"
              style={{ width: `${Math.min(((parseFloat(stats.currentPrice)-parseFloat(stats.startPrice))/(parseFloat(stats.endPrice)-parseFloat(stats.startPrice)))*100,100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>${Number(stats.startPrice).toFixed(0)}</span>
            <span className="text-cyan-500 font-medium">Current</span>
            <span>${Number(stats.endPrice).toFixed(0)}</span>
          </div>
        </div>
        {/* Mining Status Card */}
        <div className="flex flex-col bg-white/80 dark:bg-gray-900/80 rounded-lg p-3 border border-gray-200 dark:border-gray-800 shadow-sm min-w-[220px] max-w-xs flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-gray-700 dark:text-gray-200">Mining Status</span>
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${stats.miningEnded ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-200'}`}>
              {stats.miningEnded ? (<><FaCheckCircle /> Ended</>) : (<><span className="animate-pulse w-2 h-2 bg-cyan-400 rounded-full"></span> Active</>)}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center my-2">
            <svg viewBox="0 0 100 100" className="w-16 h-16">
              <circle className="stroke-gray-200 dark:stroke-gray-800" cx="50" cy="50" r="45" strokeWidth="8" fill="none" />
              <circle 
                className="stroke-cyan-400"
                cx="50" 
                cy="50" 
                r="45"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${miningProgress * 2.83} 283`}
                style={{ strokeLinecap: 'round' }}
              />
            </svg>
            <div className="text-center mt-1">
              <span className="block text-lg font-bold text-cyan-600 dark:text-cyan-300">{miningProgress.toFixed(1)}%</span>
              <span className="block text-xs text-gray-400">Mined</span>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <div>
              <span className="font-bold text-gray-700 dark:text-gray-200">{Number(stats.minedTokens).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              <span className="ml-1">Mined</span>
            </div>
            <div>
              <span className="font-bold text-gray-700 dark:text-gray-200">{Number(stats.totalSupply).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              <span className="ml-1">Total</span>
            </div>
          </div>
        </div>
      </div>
      {/* User Holdings Card */}
      <div className="flex flex-col bg-white/80 dark:bg-gray-900/80 rounded-lg p-3 border border-gray-200 dark:border-gray-800 shadow-sm mt-4 max-w-md">
        <div className="flex items-center gap-2 mb-2">
          <FaCoins className="text-emerald-500" />
          <h3 className="font-semibold text-gray-700 dark:text-gray-200">Treasury & Holdings</h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2 p-2 rounded bg-emerald-50 dark:bg-emerald-900/20">
            <FaGem className="text-emerald-400" />
            <div>
              <span className="block text-xs text-gray-500">Treasury Balance</span>
              <span className="block font-bold text-emerald-700 dark:text-emerald-200">{Number(stats.totalSupply).toLocaleString(undefined, {maximumFractionDigits: 0})} DOLP</span>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-cyan-50 dark:bg-cyan-900/20">
            <FaCoins className="text-cyan-400" />
            <div>
              <span className="block text-xs text-gray-500">Circulating Supply</span>
              <span className="block font-bold text-cyan-700 dark:text-cyan-200">{Number(stats.minedTokens).toLocaleString(undefined, {maximumFractionDigits: 0})} DOLP</span>
            </div>
          </div>
        </div>
      </div>
      {/* Network Info */}
      <div className="flex items-center gap-3 mt-4 p-3 rounded-lg bg-gradient-to-r from-cyan-50 to-purple-50 dark:from-cyan-900/30 dark:to-purple-900/20 border border-gray-200 dark:border-gray-800 max-w-md">
        <FaRocket className="text-purple-400 text-xl" />
        <div>
          <h4 className="font-semibold text-gray-700 dark:text-gray-200">Powered by Polygon</h4>
          <p className="text-xs text-gray-500 dark:text-gray-300">Fast, low-cost transactions with Ethereum-level security</p>
        </div>
      </div>
    </section>
  );
}
